import type { QueryResult } from "@apollo/client";
import { clsx } from "clsx";
import { nanoid } from "nanoid";
import { equals, filter, is } from "ramda";
import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  Badge,
  Callout,
  CalloutIntent,
  Description,
  FormError,
  HStack,
  Item,
  Label,
  LoadingBar,
  MenuOptionItem,
  Select,
  Spacer,
  TriggerButton,
  TriggerButtonSize,
  VStack,
} from "widgets";
import formStyle from "widgets/form.module.css";
import { useDataAttributes, useVisuallyLoading } from "widgets/hooks";
import { mapIndexed } from "widgets/utils";

import { createLockedSamplesWarning } from "@/app-utils/sample.ts";
import type { Exact, InputSampleFileMappingInput, InputSampleValueInput, ProjectFile } from "@/graphql.ts";
import { FileTile } from "@/modules/files/components/FileTile.tsx";
import { useGetFilesTileInfoLazyQuery } from "@/modules/files/graphql/files-queries.gql.ts";
import { SampleFileEditorControlledDialog } from "@/modules/workflow/components/SampleFileEditorControlledDialog";
import { SampleGridControlledDialog } from "@/modules/workflow/components/SampleGridControlledDialog.tsx";
import { type GetSampleTypeQuery, useGetSampleTypeQuery } from "@/modules/workflow/graphql/workflow.gql.ts";
import { getSampleFileMappings } from "@/modules/workflow/utils/sampleFileMappings.ts";
import { ATTRIBUTE_TYPE, COLUMN_SET } from "@/settings/settings.ts";
import {
  MicroSampleTile,
  type MinimalSampleTileInfo,
} from "@/ui-modules/entities/samples/components/MinimalSampleTile.tsx";
import { useGetMinimalSampleTilesLazyQuery } from "@/ui-modules/entities/samples/graphql/samples-queries.gql.ts";

export interface SampleSelectionInputProps {
  label?: string;
  description?: ReactNode;
  selection: InputSampleValueInput | null;
  error?: Error;
  isDisabled: boolean;
  isRequired?: boolean;
  extraAction?: ReactElement;
  sampleTypeId: number | undefined;
  isAttributeSelectDisabled?: boolean;

  onSelect(selection: InputSampleValueInput | null): void;
}

type SampleTileInfo = NonNullable<MinimalSampleTileInfo>;

export const SampleSelectionInput: FC<SampleSelectionInputProps> = (props) => (
  <SampleSelectionProvider>
    <SampleSelectionInputRender {...props} />
  </SampleSelectionProvider>
);

const SampleSelectionInputRender: FC<SampleSelectionInputProps> = (props) => {
  const { hasInconsistentNumberOfFiles, setFileCounts, isLoading, setLoadingFileIds, setLoadingSampleTiles } =
    useContext(SampleSelectionContext);
  const isLongLoading = useVisuallyLoading(isLoading, 300);

  const [fetchSampleTiles] = useGetMinimalSampleTilesLazyQuery();
  const [sampleTiles, setSampleTiles] = useState<SampleTileInfo[]>([]);

  const [showSampleSelectionDialog, setShowSampleSelectionDialog] = useState(false);
  const [showSampleEditingDialog, setShowSampleEditingDialog] = useState(false);

  const [selectedAttributeId, setSelectedAttributeId] = useState<InputSampleValueInput["attributeId"]>(
    props.selection?.attributeId,
  );
  const [selectedSampleFileMappings, setSelectedSampleFileMappings] = useState<
    InputSampleValueInput["sampleFileMappings"]
  >(props.selection?.sampleFileMappings);

  const sampleIds = useMemo(
    () => selectedSampleFileMappings?.map((mapping) => mapping.sampleId) ?? null,
    [selectedSampleFileMappings],
  );

  useEffect(() => {
    setFileCounts(new Map(sampleTiles.map((tile) => [String(tile.id), 0])));
  }, [sampleTiles, setFileCounts]);

  useEffect(() => {
    if (props.selection == null) {
      setSelectedAttributeId(null);
      setSelectedSampleFileMappings(null);
      return;
    }
    if (props.selection.attributeId !== selectedAttributeId) {
      setSelectedAttributeId(props.selection.attributeId);
    }

    if (!equals(props.selection.sampleFileMappings, selectedSampleFileMappings)) {
      setSelectedSampleFileMappings(props.selection.sampleFileMappings);
    }
  }, [props.selection, selectedAttributeId, selectedSampleFileMappings]);

  const onFilesSelectionChanged = useCallback(
    async (sampleIds: number[]) => {
      if (selectedAttributeId) {
        setLoadingFileIds(true);

        const sampleFileMappings = await getSampleFileMappings(selectedAttributeId, sampleIds);

        const sampleValueInput = {
          attributeId: selectedAttributeId,
          sampleFileMappings,
        } satisfies InputSampleValueInput;

        props.onSelect(sampleValueInput);
        setLoadingFileIds(false);
      } else {
        const sampleValueInput = {
          attributeId: undefined,
          sampleFileMappings:
            sampleIds?.map(
              (sampleId) =>
                ({
                  sampleId: sampleId,
                  fileIds: [],
                }) satisfies InputSampleFileMappingInput,
            ) ?? [],
        } satisfies InputSampleValueInput;

        props.onSelect(sampleValueInput);
      }
    },
    [props.onSelect, selectedAttributeId, setLoadingFileIds],
  );

  // Reset - Force reload the files on load + update on attribute change.
  // You want to analyze the samples, not the files. If we didn't resync then we'd have the case where you're mapping results back to Tracker.
  // Then you would have results in there for a file that wasn't even associated to the samples at the time the results were generated.
  useEffect(() => {
    if (sampleIds) {
      void onFilesSelectionChanged(sampleIds);
    }
  }, [selectedAttributeId]);

  useEffect(() => {
    setLoadingSampleTiles(true);

    // Just remove visually what changed in case of some deletions, before reloading all the data
    // If we don't do that, old samples are visually kept until the async fetch below finishes.
    if (sampleIds != undefined) {
      setSampleTiles((existingSampleTiles) => {
        return existingSampleTiles.filter((tile) => {
          if (sampleIds.includes(tile.id)) {
            return true;
          }
          return false;
        });
      });
    }

    if (is(Array, selectedSampleFileMappings) && selectedSampleFileMappings.length > 0) {
      // if we have a visible sample it has a valid id
      const visibleSampleIds = selectedSampleFileMappings
        .filter((sampleFileMapping) => sampleFileMapping.sampleId > 0)
        .map((sampleInput) => sampleInput.sampleId);

      // fetch these visible samples
      void fetchSampleTiles({
        variables: { sampleIds: visibleSampleIds },
      }).then((data) => {
        const samples: SampleTileInfo[] =
          data.data?.sample?.samples?.filter((sample): sample is SampleTileInfo => sample != undefined) ?? [];

        // preserve the original order
        const tiles = selectedSampleFileMappings.map((mapping) => {
          const sampleTileInfo = samples.find((sample) => sample.id === mapping.sampleId);
          return (
            sampleTileInfo ?? {
              id: mapping.sampleId,
              hidden: mapping.sampleId === -1,
              deleted: mapping.sampleId === -2,
            }
          );
        });

        setSampleTiles(filter((f): f is SampleTileInfo => f != undefined, tiles ?? []));
        setLoadingSampleTiles(false);
      });
    } else {
      setSampleTiles([]);
      setLoadingSampleTiles(false);
    }
  }, [selectedSampleFileMappings, sampleIds, fetchSampleTiles, setLoadingSampleTiles]);

  const dataAttributes = useDataAttributes(props);

  // attribute data needs to be available on this level in order to determine the column set of the selected attribute (and potentially disabling the "Attach Files" button)
  const attributeData = useGetSampleTypeQuery({
    variables: { sampleTypeId: props.sampleTypeId!, excludeAnalytical: false },
    skip: props.sampleTypeId == null,
  });

  const analyticalAttributeSelected = useMemo(() => {
    return (
      attributeData?.data?.sample.sampleType.sampleAttributes.find((a) => a.id === selectedAttributeId)?.columnSet
        .id === COLUMN_SET.COS_ANALYTICAL_ID.id
    );
  }, [attributeData, selectedAttributeId]);

  const analyticalAttributeSelectedTooltip = analyticalAttributeSelected
    ? "Attaching files to analytical attributes is not supported"
    : null;

  const lockedSampleCount = useMemo(() => sampleTiles.filter((tile) => tile.locked).length, [sampleTiles]);

  return (
    <>
      <VStack
        className={formStyle.input_layout}
        data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}multi-select__button`}
      >
        {!!props.label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label>{props.label}</Label>
            <Spacer />
            {!props.isRequired && <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />}
          </HStack>
        )}
        <Description
          data-testid="sample-selection-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: props.label != undefined && !props.isRequired,
          })}
        >
          {props.description}
        </Description>
        <div className={formStyle.input_layout_column1}>
          <SampleFileSelectionAttributeInput
            attributeData={attributeData}
            onAttributeChange={(id) => {
              // Just change the attribute id, to go fast because it's used as selectedAttribute
              props.onSelect({
                attributeId: id,
                sampleFileMappings: props.selection?.sampleFileMappings,
              });
            }}
            selectedAttribute={props.selection?.attributeId}
            sampleTypeId={props.sampleTypeId}
            isDisabled={props.isDisabled || !!props.isAttributeSelectDisabled}
            key={props.sampleTypeId}
          />
        </div>
        {!!props.error && <FormError data-testid="sample-select-field-error">{props.error.message}</FormError>}
        <VStack
          className={formStyle.input_layout_column1}
          style={{ paddingTop: 7, marginBottom: 7, position: "relative" }}
        >
          <LoadingBar isLoading={isLongLoading} topPosition />
          {mapIndexed(
            (sample, index) => (
              <SampleFileMapping
                id={sample.id > 0 ? String(sample.id) : nanoid()}
                sample={sample}
                sampleIndex={index}
                sampleFileMappings={selectedSampleFileMappings}
                onChange={(mapping) =>
                  props.onSelect({
                    attributeId: selectedAttributeId,
                    sampleFileMappings: mapping,
                  })
                }
                isDisabled={props.isDisabled || isLoading}
                key={sample.id}
                attachFilesButton={
                  <div>
                    <ActionButton
                      label="Attach Some Files..."
                      isDisabled={
                        props.isDisabled || props.sampleTypeId == null || isLoading || analyticalAttributeSelected
                      }
                      tooltip={analyticalAttributeSelectedTooltip}
                      onPress={() => setShowSampleEditingDialog(true)}
                      size={ActionButtonSize.Small}
                      intent={ActionButtonIntent.Secondary}
                    />
                  </div>
                }
              />
            ),
            sampleTiles,
          )}
        </VStack>
        {!!hasInconsistentNumberOfFiles && (
          <Callout className={formStyle.input_layout_column1} intent={CalloutIntent.Warning} title="Notice:" inline>
            The number of files varies across samples.
          </Callout>
        )}
        <HStack className={formStyle.input_layout_column1} style={{ width: "100%" }}>
          <ActionButton
            label="Select Samples"
            isDisabled={props.isDisabled || props.sampleTypeId == null || isLoading}
            onPress={() => setShowSampleSelectionDialog(true)}
            // isLoading={loading}
            style={{ flex: 1 }}
            intent={(sampleIds?.length ?? 0) > 0 ? ActionButtonIntent.Outlined : ActionButtonIntent.Primary}
          />
          {(sampleIds?.length ?? 0) > 0 && (
            <ActionButton
              label="Attach Files"
              // we need to disable this button if an analytical attribute is selected because the editing grid does not support them (SEL10-1866)
              isDisabled={props.isDisabled || props.sampleTypeId == null || isLoading || analyticalAttributeSelected}
              onPress={() => setShowSampleEditingDialog(true)}
              tooltip={analyticalAttributeSelectedTooltip}
              // isLoading={loading}
              style={{ flex: 1 }}
            />
          )}
        </HStack>
        {!!props.extraAction && <HStack className={formStyle.input_layout_column2}>{props.extraAction}</HStack>}
        {!!lockedSampleCount && (
          <Callout className={formStyle.input_layout_column1} intent={CalloutIntent.Warning} title="Warning:" inline>
            {createLockedSamplesWarning(lockedSampleCount)}
          </Callout>
        )}
      </VStack>

      {props.sampleTypeId != undefined && (
        <>
          <SampleGridControlledDialog
            isOpen={showSampleSelectionDialog}
            onClose={() => setShowSampleSelectionDialog(false)}
            onApply={(sampleIds) => {
              void onFilesSelectionChanged(sampleIds);
            }}
            isDisabled={props.isDisabled}
            selection={selectedSampleFileMappings?.map((sampleFileMapping) => sampleFileMapping.sampleId) ?? []}
            sampleTypeId={props.sampleTypeId}
          />
          {sampleIds != undefined && (
            <SampleFileEditorControlledDialog
              isOpen={showSampleEditingDialog}
              onClose={() => {
                setShowSampleEditingDialog(false);
                // refresh the list of files
                void onFilesSelectionChanged(sampleIds);
              }}
              sampleIds={sampleIds}
              fileAttributeId={props.selection?.attributeId ?? undefined}
              sampleTypeId={props.sampleTypeId}
            />
          )}
        </>
      )}
    </>
  );
};

interface SampleFileSelectionAttributeInputProps {
  attributeData: QueryResult<GetSampleTypeQuery, Exact<{ sampleTypeId: number; excludeAnalytical: boolean }>>;
  selectedAttribute?: number | null;
  className?: string;
  sampleTypeId: number | undefined;
  isDisabled: boolean;

  onAttributeChange(attributeId?: number): void;
}

const SampleFileSelectionAttributeInput: FC<SampleFileSelectionAttributeInputProps> = (props) => {
  const { data, loading, error } = props.attributeData;
  // get only file attributes
  const attributes = useMemo(() => {
    return data?.sample?.sampleType?.sampleAttributes
      ?.filter((attribute) => {
        return (
          attribute.columnSet.id !== COLUMN_SET.COS_PARENT_ID.id &&
          attribute.attributeType.id === ATTRIBUTE_TYPE.FILE &&
          !attribute.representedColumn && // e.g. remove the Sample Report attribute which is not editable
          (attribute.editable || attribute.modifiable)
        );
      })
      .map((attribute) => ({
        id: String(attribute.id),
        name: attribute.attributeName,
        group: attribute.groupName,
      }));
  }, [data]);

  return (
    <Select
      selectedKey={props.selectedAttribute == null ? null : String(props.selectedAttribute)}
      isDisabled={props.isDisabled || loading || !attributes?.length}
      isRequired
      placeholder={
        loading ? "Loading attributes..." : attributes?.length ? "Select an attribute" : "No attributes available"
      }
      onSelectionChange={(key) => {
        props.onAttributeChange(key ? Number(key) : undefined);
      }}
      items={attributes ?? []}
      errorMessage={error?.message}
      isInvalid={!!error}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          <MenuOptionItem title={item.name} description={item.group} />
        </Item>
      )}
    </Select>
  );
};

interface SampleFileMappingProps {
  id: string; // can be from nanoid
  sample: SampleTileInfo;
  sampleIndex: number;
  sampleFileMappings: InputSampleValueInput["sampleFileMappings"];
  className?: string;
  isDisabled: boolean;
  attachFilesButton?: ReactElement;

  onChange(mappings: InputSampleValueInput["sampleFileMappings"]): void;
}

const SampleFileMapping: FC<SampleFileMappingProps> = (props) => {
  const { setFileCounts, isLoading, setLoadingFilesRC } = useContext(SampleSelectionContext);
  const { sample } = props;
  const [collapsed, setCollapsed] = useState(true);

  const fileIds = useMemo(
    () => props.sampleFileMappings?.find((mapping) => mapping.sampleId === sample.id)?.fileIds ?? [],
    [props.sampleFileMappings, sample.id],
  );

  const [fetchFileTiles] = useGetFilesTileInfoLazyQuery();
  const [, startTransition] = useTransition();

  const [fileTiles, setFileTiles] = useState<NonNullable<ProjectFile>[]>([]);

  useEffect(() => {
    const restrictedSample = sample.deleted || sample.hidden;
    if (!restrictedSample) {
      const restrictedFileIds = fileIds.filter((fileId) => fileId < 0);
      const visibleFileIds = fileIds.filter((fileId) => fileId > 0);

      const restrictedFiles = restrictedFileIds.map((id) => ({
        id,
        hidden: id == -1,
        deleted: id == -2,
      })) as unknown as ProjectFile[];

      if (visibleFileIds.length > 0) {
        setLoadingFilesRC((refCounter) => refCounter + 1);
        void fetchFileTiles({ variables: { fileIds: visibleFileIds } })
          .then((data) => {
            const files = data?.data?.projectFilesById ?? [];
            startTransition(() => {
              setFileTiles(files.concat(restrictedFiles) as unknown as ProjectFile[]);
            });
          })
          .finally(() => {
            setLoadingFilesRC((refCounter) => refCounter - 1);
          });
      } else {
        startTransition(() => {
          setFileTiles(restrictedFiles);
        });
      }
    }
  }, [sample, fileIds, fetchFileTiles, setLoadingFilesRC]);

  useEffect(() => {
    if (fileTiles.length > 0 && !isLoading) {
      setFileCounts((data) => {
        const newCounts = new Map(data);
        newCounts.set(props.id, fileTiles.length);
        return newCounts;
      });
    }
  }, [fileTiles, isLoading, setFileCounts, props.id]);

  return (
    <VStack wrap spacing={3}>
      <HStack key={props.id}>
        <MicroSampleTile sample={sample} style={{ width: "100%", flex: 1 }} forceExternal />
        <TriggerButton
          icon="expand_all"
          label=""
          aria-label="Show / hide files"
          onPress={() => {
            setCollapsed((c) => !c);
          }}
          badge={String(fileTiles.length)}
          badgeIntent={fileTiles.length === 0 || fileTiles.some((file) => file.id <= 0) ? "danger" : "primary"}
          size={TriggerButtonSize.Small}
        />
      </HStack>
      {sample.id > 0 && !collapsed && (
        <VStack spacing={3} style={{ paddingLeft: "30px", maxWidth: "100%", marginBottom: 5 }}>
          {fileTiles.length > 0
            ? fileTiles.map((file) => (
                <FileTile file={file} key={file.id > 0 ? file.id : nanoid()} lightweight={true} forceExternal={true} />
              ))
            : props.attachFilesButton}
        </VStack>
      )}
    </VStack>
  );
};

interface SampleSelectionContextProps {
  hasInconsistentNumberOfFiles: boolean;
  isLoading: boolean;
  setLoadingFilesRC: Dispatch<SetStateAction<number>>;
  setLoadingFileIds: Dispatch<SetStateAction<boolean>>;
  setLoadingSampleTiles: Dispatch<SetStateAction<boolean>>;
  setFileCounts: Dispatch<SetStateAction<Map<string, number>>>;
}

const SampleSelectionContext = createContext<SampleSelectionContextProps>({} as SampleSelectionContextProps);

const SampleSelectionProvider: FC<PropsWithChildren> = ({ children }) => {
  const [filesCounts, setFileCounts] = useState<Map<string, number>>(new Map());
  const [isLoadingFileIds, setLoadingFileIds] = useState(false);
  const [isLoadingSampleTiles, setLoadingSampleTiles] = useState(false);
  const [isLoadingFilesRC, setLoadingFilesRC] = useState(0);

  const isLoading = useMemo(() => {
    return isLoadingFileIds || isLoadingSampleTiles || isLoadingFilesRC > 0;
  }, [isLoadingFileIds, isLoadingSampleTiles, isLoadingFilesRC]);

  const hasInconsistentNumberOfFiles = useMemo(() => {
    return !areFileCountsSame(filesCounts);
  }, [filesCounts]);

  return (
    <SampleSelectionContext.Provider
      value={{
        hasInconsistentNumberOfFiles,
        isLoading,
        setFileCounts,
        setLoadingFilesRC,
        setLoadingFileIds,
        setLoadingSampleTiles,
      }}
    >
      {children}
    </SampleSelectionContext.Provider>
  );
};

function areFileCountsSame(fileCounts: Map<string, number>): boolean {
  if (fileCounts.size === 0) {
    return true;
  }

  // Get an iterator for the values
  const iterator = fileCounts.values();

  // Get the first value to compare others against
  const firstValue = iterator.next().value;

  // Iterate through the remaining values and check if they match the first value
  for (const value of iterator) {
    if (value !== firstValue) {
      return false;
    }
  }

  return true;
}
